import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [invite, setInvite] = useState(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/auth/invitation/${token}`)
      .then(({ data }) => {
        setInvite(data);
        setFullName(data.invitation?.full_name || "");
        setState("valid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!fullName.trim()) return setError("Please enter your full name.");
    if (password.length < 8) return setError("Please use at least 8 characters.");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/accept-invite", {
        token, password, full_name: fullName.trim(),
      });
      localStorage.setItem("lf_token", data.token);
      setState("done");
      setTimeout(() => { window.location.href = "/app/dashboard"; }, 1200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "This invitation could not be accepted.");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8">
        {state === "checking" && (
          <p className="text-sm text-[#757675] flex items-center gap-2" data-testid="invite-checking">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your invitation...
          </p>
        )}

        {state === "invalid" && (
          <div data-testid="invite-invalid">
            <h1 className="text-2xl text-[#1A1B1A] mb-2" style={H}>This invitation is no longer valid</h1>
            <p className="text-sm text-[#4A4B4A] mb-6">
              Invitations can only be used once and expire after 7 days. Ask the clinic to send a new one.
            </p>
            <button onClick={() => navigate("/app/login")} data-testid="invite-go-login"
              className="px-4 py-2.5 rounded-xl bg-[#205B5A] text-white text-sm">Go to sign in</button>
          </div>
        )}

        {state === "done" && (
          <div data-testid="invite-success">
            <CheckCircle2 className="w-8 h-8 text-[#205B5A] mb-3" />
            <h1 className="text-2xl text-[#1A1B1A] mb-2" style={H}>You're in</h1>
            <p className="text-sm text-[#4A4B4A]">Taking you to the clinic...</p>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit}>
            <h1 className="text-2xl text-[#1A1B1A] mb-2" style={H}>
              Join {invite?.clinic_name || "the clinic"}
            </h1>
            <p className="text-sm text-[#4A4B4A] mb-6">
              You've been invited as a <span className="capitalize">{invite?.invitation?.role}</span> for{" "}
              <span className="text-[#1A1B1A]">{invite?.invitation?.email}</span>.
              {invite?.has_account && " Enter your existing LumeroFlow password to join."}
            </p>
            <label className="text-xs text-[#757675]">Full name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)}
              data-testid="invite-name-input"
              className="mt-1 mb-4 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
            <label className="text-xs text-[#757675]">
              {invite?.has_account ? "Your existing password" : "Create a password"}
            </label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="invite-password-input" autoComplete="new-password"
              className="mt-1 mb-4 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
            {error && <p className="text-xs text-red-600 mb-3" data-testid="invite-error">{error}</p>}
            <button type="submit" disabled={saving} data-testid="invite-submit-btn"
              className="w-full px-4 py-2.5 rounded-xl bg-[#205B5A] text-white text-sm disabled:opacity-50">
              {saving ? "Joining..." : "Accept invitation"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
